export const KEEP_OLD_VIEW_STORAGE_KEY = '__partner360-planning-keep-old-view'

export const KEEP_NEW_VIEW_STORAGE_KEY = '__partner360-planning-keep-new-view'

export const isOldViewKept = () => localStorage.getItem(KEEP_OLD_VIEW_STORAGE_KEY) === 'true'

export const isNewViewKept = () => localStorage.getItem(KEEP_NEW_VIEW_STORAGE_KEY) === 'true'

let isOldViewKeptOnLoad: boolean | undefined

/** Read once per page load, so every consumer resolves a URL without `v` to the same view even
 *  after the user changes the choice mid-session. */
export const getIsOldViewKeptOnLoad = () => {
  isOldViewKeptOnLoad ??= isOldViewKept()

  return isOldViewKeptOnLoad
}

const VIEW_PROMO_SHOWN_STORAGE_KEY = '__partner360-planning-view-promo-shown'

const readPartnersShownViewPromo = (): string[] => {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(VIEW_PROMO_SHOWN_STORAGE_KEY) ?? '[]')

    return Array.isArray(value) ? value.filter((it): it is string => typeof it === 'string') : []
  } catch {
    return []
  }
}

/** The view promo is offered once per partner opened in this browser session, whichever view
 *  opens first; switching views or opening the partner again does not bring it back, and it is
 *  never offered once the user has kept either view as the default. */
export const isViewPromoDue = (partnerId: string) =>
  Boolean(partnerId) &&
  !isOldViewKept() &&
  !isNewViewKept() &&
  !readPartnersShownViewPromo().includes(partnerId)

export const markViewPromoShown = (partnerId: string) => {
  try {
    const shown = readPartnersShownViewPromo()

    if (!shown.includes(partnerId)) {
      sessionStorage.setItem(VIEW_PROMO_SHOWN_STORAGE_KEY, JSON.stringify([...shown, partnerId]))
    }
  } catch {
    /* Without storage the promo may come back on the next view; nothing else depends on it. */
  }
}

let isViewPromoOpen = false
const viewPromoListeners = new Set<() => void>()

/** Published by the view switcher while its promo card is on screen, so the page under it can
 *  hold back what would open over the card. */
export const setViewPromoOpen = (isOpen: boolean) => {
  if (isViewPromoOpen === isOpen) {
    return
  }

  isViewPromoOpen = isOpen
  viewPromoListeners.forEach((listener) => listener())
}

export const subscribeToViewPromo = (listener: () => void) => {
  viewPromoListeners.add(listener)

  return () => {
    viewPromoListeners.delete(listener)
  }
}

/** Open, or still due because the switcher has not mounted yet. */
export const isViewPromoPending = (partnerId: string) =>
  isViewPromoOpen || isViewPromoDue(partnerId)
